import { LegalPage, LegalProse, LegalSection } from '../components/LegalProse';

/**
 * /safety — the published crisis protocol and the AI notice (2026-09-17 拍板).
 *
 * California SB 243 asks an operator of a companion chatbot to publish, on its
 * website, the protocol it follows when a user expresses suicidal ideation or
 * self-harm. Ember is a thinking partner, not a companion, and does not concede
 * the label; it publishes the protocol regardless, because the protocol is what
 * matters. This page says what the product actually does — every fact here is
 * edge behaviour, not policy prose:
 *   detection   → the card reader's `safety` reading on every user turn
 *                 (brain where_card, edge accounts.refreshCard)
 *   placement   → conversation-service.flagSafety, once per 24 hours of
 *                 flagged turns; the web card, the SMS text, the spoken line
 *   the words   → src/edge/ember/system-notices.js (CRISIS_LINES, by country since 2026-10-29) —
 *                 the same on every door
 *   the count   → safety.crisis_referred (the yearly report from 2027-07-01)
 *   others      → 2026-10-12: a risk of hurting someone else, read as its own
 *                 kind (harm_to_others) — conversation-service.flagHarm,
 *                 CRISIS_LINES `harm`, safety.harm_referred (not the SB 243 count)
 *   the writer  → extraction.txt: no method or means detail is ever written
 *                 into an Ember or shown to another person
 *   the talk    → 2026-10-20: talk_experience_as / _ap (ps INSTRUCTION 2.HU)
 *                 do not talk through how to hurt oneself or end one's life,
 *                 in a story either
 * Change the code and this page changes with it.
 */
export default function SafetyProtocol() {
  return (
    <LegalPage title="Safety on Ember" dateline="Ember is a service of Oriscen, Inc. Last updated: October 12, 2026">

        <LegalProse>
          <p>
            Ember is an AI you talk with about the real situations of your own life. It is not a
            person, not a therapist, and not a crisis service. This page says, in plain words, what
            the product itself does about that — the notice it gives, and what happens when someone
            says they may hurt themselves or someone else.
          </p>
          <p>
            <strong>If you are in danger right now:</strong> call or text <strong>988</strong> (the
            Suicide &amp; Crisis Lifeline, United States, any time), text <strong>HOME</strong> to{' '}
            <strong>741741</strong> (Crisis Text Line), or call <strong>911</strong>.
          </p>

          <LegalSection title="Ember is an AI">
            <p>
              Ember has a name and a voice, remembers what you have told it, and sometimes texts you
              first. It is still an AI and not a person, with no feelings and no life of its own; it
              is a thinking partner for the situations of your life, not a companion, and it does not
              stand in for a relationship. The product says what Ember is on every door, once, and
              leaves it standing:
            </p>
            <ul>
              <li>On the web, a line under the conversation: &ldquo;Ember is an AI. It can be wrong.&rdquo;</li>
              <li>
                By text message, a line in the welcome when you join and in the reply to HELP.
              </li>
              <li>
                On a call, the spoken words that open every call: that the call is recorded and
                that your voice is processed by AI, not by a human.
              </li>
            </ul>
            <p>
              Ember itself is told that it is an AI, that it does not present itself as a friend,
              therapist or doctor, and that it does not claim feelings or a life. Asked whether it
              is a person, it says what it is.
            </p>
          </LegalSection>

          <LegalSection title="When someone may be at risk of harming themselves">
            <p>
              Beside every reply, a separate reading looks at the words the person just said and
              answers one question among others: do these words carry a risk of self-harm — an
              intention or a wish to die or to hurt oneself, or immediate danger? Telling about a
              past time, talking about someone else, or a figure of speech is not that.
            </p>
            <p>When the answer is yes, the product does two things, and neither is left to Ember:</p>
            <ol>
              <li>
                It places the crisis resources in front of the person, in the product&rsquo;s own
                voice: on the web as a card in the conversation right after the words it answers; by
                text as its own message; on a call spoken before Ember&rsquo;s next words. The card
                says: &ldquo;{`If you are thinking about harming yourself, you can call or text 988 — the Suicide & Crisis Lifeline — at any time, or text HOME to 741741 to reach the Crisis Text Line. If you are in immediate danger, call 911. Ember is an AI and cannot send help to you.`}&rdquo;
                It is placed once, and again if the risk is still read a day or more later.
              </li>
              <li>
                It marks the conversation, so that Ember is told on every later turn — on every
                door, however much has been said since — that this person has spoken of harming
                themselves. Ember&rsquo;s own instruction is to say plainly when someone&rsquo;s
                safety is at risk and to point them to real help. It does not talk through ways a
                person could hurt themselves or end their life &mdash; not methods, means or plans,
                in a story or otherwise.
              </li>
            </ol>
            <p>
              Each placement is counted, without the person&rsquo;s words or identity, so that the
              number can be reported as the law asks.
            </p>
          </LegalSection>

          <LegalSection title="When someone may hurt someone else">
            <p>
              The same reading also asks whether the words carry a risk of hurting another person
              — an intention or a wish to hurt someone, or a pull toward it the person says they may
              not be able to resist. Anger said as anger, violence in a story or a game, or a figure
              of speech is not that.
            </p>
            <p>
              When the answer is yes, the product places these words in front of the person the
              same way, on every door, once, and again if the risk is still read a day or more
              later: &ldquo;{`If you are thinking about hurting someone, you can call or text 988 at any time to talk it through with a trained counselor. If anyone is in immediate danger, call 911. Ember is an AI and cannot send help to you.`}&rdquo;
              It also marks the conversation, so that Ember is told on every later turn that this
              was said. Each placement is counted on its own, without the person&rsquo;s words or
              identity.
            </p>
          </LegalSection>
          <LegalSection title="What Ember will not produce">
            <p>
              When what you told is written into an Ember, the writer leaves out any usable method
              or means detail for suicide or self-harm: having wanted to die, having survived,
              belongs in a person&rsquo;s own account; the how does not, and it is never shown to
              another person. Ember does not present itself as a licensed mental health professional
              and does not provide therapy.
            </p>
          </LegalSection>

          <LegalSection title="Who Ember is for">
            <p>
              Ember is for adults 18 and over. It may not be suitable for some minors, and it is not
              designed for them: there is no children&rsquo;s version. If you believe a child is
              using Ember, write to <a href="mailto:contact@oriscen.ai">contact@oriscen.ai</a>. Our
              standards against child sexual abuse and exploitation are at{' '}
              <a href="/child-safety" data-log="legal_child_safety_link">ember.space/child-safety</a>.
            </p>
          </LegalSection>

          <LegalSection title="Questions">
            <p>
              <a href="mailto:contact@oriscen.ai">contact@oriscen.ai</a>. Oriscen, Inc., 2261 Market
              Street STE 86857, San Francisco, CA 94114.
            </p>
          </LegalSection>
        </LegalProse>
    </LegalPage>
  );
}
